import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type NotificationShape } from '@/lib/api';

const LIST_KEY = ['notifications', 'list'] as const;
const UNREAD_KEY = ['notifications', 'unread-count'] as const;

export function useNotificationsQuery(token: string, enabled = true) {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: () => api.notifications.list(token),
    staleTime: 30_000,
    enabled: enabled && token.length > 0,
  });
}

export function useUnreadCountQuery(token: string, enabled = true) {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => api.notifications.unreadCount(token),
    staleTime: 30_000,
    enabled: enabled && token.length > 0,
  });
}

export function useMarkNotificationReadMutation(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.notifications.markRead(token, id),
    onSuccess: (_data, id) => {
      // Remove from unread count optimistically.
      queryClient.setQueryData<{ unread: number }>(UNREAD_KEY, (prev) =>
        prev ? { unread: Math.max(0, prev.unread - 1) } : prev,
      );
      queryClient.setQueryData<{ notifications: NotificationShape[] }>(LIST_KEY, (prev) =>
        prev
          ? {
              notifications: prev.notifications.map((n) =>
                n.id === id && n.readAt === null ? { ...n, readAt: Date.now() } : n,
              ),
            }
          : prev,
      );
    },
  });
}

export function useMarkAllNotificationsReadMutation(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.notifications.markAllRead(token),
    onSuccess: () => {
      queryClient.setQueryData<{ unread: number }>(UNREAD_KEY, { unread: 0 });
      queryClient.setQueryData<{ notifications: NotificationShape[] }>(LIST_KEY, (prev) =>
        prev
          ? {
              notifications: prev.notifications.map((n) =>
                n.readAt ? n : { ...n, readAt: Date.now() },
              ),
            }
          : prev,
      );
    },
  });
}

/* ─── Cache helpers used by the WS subscriber ──────────────────────────── */

export const notificationsCache = {
  /** Prepend an incoming notification + bump the unread count. */
  prepend(
    queryClient: import('@tanstack/react-query').QueryClient,
    notification: NotificationShape,
  ) {
    queryClient.setQueryData<{ notifications: NotificationShape[] }>(LIST_KEY, (prev) => {
      const list = prev?.notifications ?? [];
      if (list.some((n) => n.id === notification.id)) return prev;
      return { notifications: [notification, ...list] };
    });
    queryClient.setQueryData<{ unread: number }>(UNREAD_KEY, (prev) => ({
      unread: (prev?.unread ?? 0) + 1,
    }));
  },
};
